import { Component, ViewChild, OnInit, OnDestroy, ElementRef } from "@angular/core";
import {
  SearchFilter,
  IFilterResponse,
} from "../../../infrastructure/iFilter-component";
import { MatDialog } from "@angular/material";
import { SearchFilterComponent } from "../../search-filter/search-filter.component";
import { Router } from "@angular/router";
import { MenuService } from "../../../services/menu.service";
import { AdmissionSearchItem } from "../../../model/admission-search-item";
import { PagerControlComponent } from "../../controls/pager/pager-control.component";
import { AdmissionService } from "src/app/services/admission.service";
import { ConfirmDialogService } from "src/app/services/confirm-dialog.service";
import { AuthService } from "src/app/services/auth.service";
import { MedicalAppointmenObservationDialogComponent } from "../../dialogs/medical-appointment-observation/medical-appointment-observation-dialog.component";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { DatePipe } from "@angular/common";

interface ColumnDefinition {
  field: string;
  title: string;
  visible: boolean;
  order: number;
  width: number;
}

@Component({
  selector: "app-admission-reception-search-component",
  templateUrl: "./admission-reception-search.component.html",
  styleUrls: ["./admission-reception-search.component.css"],
  providers: [DatePipe],
})
export class AdmissionReceptionSearchComponent implements OnInit, OnDestroy {
  loaded: boolean;
  loadingAction: boolean;
  searchFilters: SearchFilter[];

  @ViewChild("pager", { static: true }) pager: PagerControlComponent;
  @ViewChild("searchFilter", { static: false }) searchFilter: SearchFilterComponent;
  @ViewChild("gridScrollContainer", { static: false }) gridScrollContainer: ElementRef<HTMLDivElement>;

  private sessionColumnsKey = "admission-reception-search.columns.v1";
  private sessionSortKey = "admission-reception-search.sort.v1";
  private sessionColumnWidthsKey = "admission-reception-search.column-widths.v1";

  columnsDefinition: ColumnDefinition[] = [
    { field: "date",                      title: "Fecha",           visible: true,  order: 0, width: 150 },
    { field: "patientLasNameFirstName",   title: "Paciente",        visible: true,  order: 1, width: 250 },
    { field: "practiceRegionName",        title: "Práctica",        visible: true,  order: 2, width: 200 },
    { field: "equipmentName",             title: "Equipo",          visible: true,  order: 3, width: 200 },
    { field: "isOverAppointment",         title: "Sobreturno",      visible: true,  order: 4, width: 110 },
    { field: "audited",                   title: "Auditado",        visible: true,  order: 5, width: 110 },
    { field: "physicianLastNameFirstName",title: "Médico actuante", visible: true,  order: 6, width: 200 },
    { field: "entityName",               title: "Entidad",          visible: true,  order: 7, width: 200 },
  ];

  get availableColumns(): ColumnDefinition[] {
    return this.columnsDefinition;
  }

  get orderedColumnsDefinition(): ColumnDefinition[] {
    return this.columnsDefinition
      .filter(c => c.visible)
      .sort((a, b) => a.order - b.order);
  }

  dataSource: AdmissionSearchItem[] = [];
  allItems: AdmissionSearchItem[] = [];
  columnFilters: { [field: string]: string } = {};

  private sortField: string = null;
  private sortDirection: "asc" | "desc" = "asc";

  private dragSourceField: string = null;
  private resizeListeners: { move: EventListener; up: EventListener } = null;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private authService: AuthService,
    private admissionService: AdmissionService,
    private confirmService: ConfirmDialogService,
    private menuService: MenuService,
    private datePipe: DatePipe
  ) {
    this.menuService.setMenuTitle("Admisión", "recepción");
  }

  ngOnInit() {
    this.restoreColumns();
    this.restoreSort();
    this.restoreColumnWidths();
  }

  ngOnDestroy() {
    this.removeResizeListeners();
  }

  // ── Column visibility ──────────────────────────────────────────────────────

  isColumnVisible(field: string): boolean {
    const col = this.columnsDefinition.find(c => c.field === field);
    return col ? col.visible : false;
  }

  setColumnVisibility(field: string, visible: boolean) {
    const col = this.columnsDefinition.find(c => c.field === field);
    if (col) {
      col.visible = visible;
      this.saveColumns();
      this.fitVisibleColumnsToContainer();
    }
  }

  showAllColumns() {
    this.columnsDefinition.forEach(c => (c.visible = true));
    this.saveColumns();
    this.fitVisibleColumnsToContainer();
  }

  private saveColumns() {
    const state = this.columnsDefinition.map(c => ({ field: c.field, visible: c.visible, order: c.order }));
    sessionStorage.setItem(this.sessionColumnsKey, JSON.stringify(state));
  }

  private restoreColumns() {
    const raw = sessionStorage.getItem(this.sessionColumnsKey);
    if (!raw) { return; }
    try {
      const state: { field: string; visible: boolean; order: number }[] = JSON.parse(raw);
      state.forEach(s => {
        const col = this.columnsDefinition.find(c => c.field === s.field);
        if (col) { col.visible = s.visible; col.order = s.order; }
      });
    } catch { }
  }

  // ── Sort ──────────────────────────────────────────────────────────────────

  sortByColumn(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
    this.saveSort();
    this.refreshGrid();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) { return "unfold_more"; }
    return this.sortDirection === "asc" ? "arrow_upward" : "arrow_downward";
  }

  private sortAllData(items: AdmissionSearchItem[]): AdmissionSearchItem[] {
    if (!this.sortField) { return items; }
    const dir = this.sortDirection === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = this.getColumnFilterValue(a, this.sortField);
      const vb = this.getColumnFilterValue(b, this.sortField);
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }

  private saveSort() {
    sessionStorage.setItem(this.sessionSortKey, JSON.stringify({ field: this.sortField, direction: this.sortDirection }));
  }

  private restoreSort() {
    const raw = sessionStorage.getItem(this.sessionSortKey);
    if (!raw) { return; }
    try {
      const s = JSON.parse(raw);
      this.sortField = s.field;
      this.sortDirection = s.direction;
    } catch { }
  }

  // ── Column filters ────────────────────────────────────────────────────────

  onColumnFilterInput(field: string, value: string) {
    this.columnFilters[field] = value;
    this.refreshGrid();
  }

  private applyColumnFilters(items: AdmissionSearchItem[]): AdmissionSearchItem[] {
    return items.filter(item => {
      return Object.keys(this.columnFilters).every(field => {
        const filterVal = this.normalizeFilterValue(this.columnFilters[field]);
        if (!filterVal) { return true; }
        const cellVal = this.normalizeFilterValue(this.getColumnFilterValue(item, field));
        return cellVal.includes(filterVal);
      });
    });
  }

  private getColumnFilterValue(item: AdmissionSearchItem, field: string): string {
    switch (field) {
      case "date":                       return this.datePipe.transform(item.DateTime, "dd/MM/yyyy HH:mm") || "";
      case "patientLasNameFirstName":    return item.PatientLastNameFirstName || "";
      case "practiceRegionName":         return item.PracticeRegionName || "";
      case "equipmentName":              return item.EquipmentName || "";
      case "isOverAppointment":          return item.IsOverAppointment ? "sí" : "no";
      case "audited":                    return item.IsAuditedYesNo ? "sí" : "no";
      case "physicianLastNameFirstName": return item.PhysicianLastNameFirstName || "";
      case "entityName":                 return item.EntityName || "";
      default:                           return "";
    }
  }

  private normalizeFilterValue(value: string): string {
    if (!value) { return ""; }
    return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  // ── Refresh ───────────────────────────────────────────────────────────────

  private refreshGrid() {
    const filtered = this.applyColumnFilters(this.allItems);
    const sorted = this.sortAllData(filtered);
    this.pager.setItems(sorted);
  }

  // ── Column resize ─────────────────────────────────────────────────────────

  getColumnWidth(field: string): number {
    const col = this.columnsDefinition.find(c => c.field === field);
    return col ? col.width : 150;
  }

  startResize(event: MouseEvent, field: string) {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const col = this.columnsDefinition.find(c => c.field === field);
    const startWidth = col ? col.width : 150;

    this.removeResizeListeners();

    const moveHandler: EventListener = (e: MouseEvent) => {
      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
      if (col) { col.width = newWidth; }
    };
    const upHandler: EventListener = () => {
      this.removeResizeListeners();
      this.saveColumnWidths();
    };

    (moveHandler as any).__admissionReceptionResizeMove = true;
    (upHandler as any).__admissionReceptionResizeUp = true;

    document.addEventListener("mousemove", moveHandler);
    document.addEventListener("mouseup", upHandler);
    this.resizeListeners = { move: moveHandler, up: upHandler };
  }

  private removeResizeListeners() {
    if (this.resizeListeners) {
      document.removeEventListener("mousemove", this.resizeListeners.move);
      document.removeEventListener("mouseup", this.resizeListeners.up);
      this.resizeListeners = null;
    }
  }

  private saveColumnWidths() {
    const widths: { [field: string]: number } = {};
    this.columnsDefinition.forEach(c => (widths[c.field] = c.width));
    sessionStorage.setItem(this.sessionColumnWidthsKey, JSON.stringify(widths));
  }

  private restoreColumnWidths() {
    const raw = sessionStorage.getItem(this.sessionColumnWidthsKey);
    if (!raw) { return; }
    try {
      const widths: { [field: string]: number } = JSON.parse(raw);
      this.columnsDefinition.forEach(c => {
        if (widths[c.field] !== undefined) { c.width = widths[c.field]; }
      });
    } catch { }
  }

  private fitVisibleColumnsToContainer() {
    setTimeout(() => {
      const container = this.gridScrollContainer && this.gridScrollContainer.nativeElement;
      const visibleColumns = this.orderedColumnsDefinition;
      if (!container || visibleColumns.length === 0) { return; }

      const actionsColumnWidth = 48;
      const availableWidth = container.clientWidth - actionsColumnWidth;
      if (availableWidth <= 0) { return; }

      const totalWidth = visibleColumns.reduce((sum, col) => sum + col.width, 0);
      if (totalWidth <= 0) { return; }

      const scale = availableWidth / totalWidth;
      const minWidth = Math.max(50, Math.floor(availableWidth / visibleColumns.length));
      let assignedWidth = 0;

      visibleColumns.forEach((col, index) => {
        const remainingColumns = visibleColumns.length - index - 1;
        const desiredWidth = Math.max(minWidth, Math.floor(col.width * scale));
        const maxWidth = availableWidth - assignedWidth - (remainingColumns * minWidth);
        const nextWidth = index === visibleColumns.length - 1
          ? availableWidth - assignedWidth
          : Math.min(desiredWidth, maxWidth);

        col.width = nextWidth;
        assignedWidth += nextWidth;
      });

      this.saveColumnWidths();
    });
  }

  // ── Column drag/drop ──────────────────────────────────────────────────────

  onHeaderDragStart(event: DragEvent, field: string) {
    this.dragSourceField = field;
    event.dataTransfer.effectAllowed = "move";
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }

  onHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    if (!this.dragSourceField || this.dragSourceField === targetField) { return; }
    const visible = this.orderedColumnsDefinition;
    const fromIdx = visible.findIndex(c => c.field === this.dragSourceField);
    const toIdx = visible.findIndex(c => c.field === targetField);
    if (fromIdx < 0 || toIdx < 0) { return; }
    moveItemInArray(visible, fromIdx, toIdx);
    visible.forEach((c, i) => {
      const col = this.columnsDefinition.find(x => x.field === c.field);
      if (col) { col.order = i; }
    });
    this.saveColumns();
    this.dragSourceField = null;
  }

  onHeaderDragEnd() {
    this.dragSourceField = null;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  onStartSearch() {}

  async patientAdmission(admissionSearchItem: AdmissionSearchItem) {
    let setSectorEquipment = false;
    const sectorId = this.authService.getSectorId();

    const diff = await this.admissionService.getLateAttentionMinutes(
      admissionSearchItem.MedicalAppointmentId
    );

    let isOk = true;
    if (diff > 0) {
      isOk = await this.confirmService
        .confirm(
          "Recepción",
          "Ya transcurrieron " +
            diff +
            " minutos del horario original. Está seguro de recepcionar?",
          "Sí",
          "No"
        )
        .toPromise();
    }

    if (
      this.authService.canViewOtherCentersReception() &&
      sectorId !== admissionSearchItem.SectorId
    ) {
      setSectorEquipment = await this.confirmService
        .confirm(
          "Recepción",
          "Está por recepcionar un turno de otra sede o sector, Desea continuar?",
          "Sí",
          "No"
        )
        .toPromise();
    }

    if (isOk) {
      if (this.authService.setSectorEquipment() || setSectorEquipment) {
        await this.admissionService.setEquipmentFromUserSector(
          admissionSearchItem.MedicalAppointmentId
        );
      }

      const observation = await this.admissionService.getObservations(
        admissionSearchItem.MedicalAppointmentId
      );

      if (
        observation.AuditoriaObservations &&
        observation.AuditoriaObservations.length > 0
      ) {
        const dialogRef = this.dialog.open(
          MedicalAppointmenObservationDialogComponent,
          {
            width: "50%",
            data: {
              observation: observation,
              onlyAuditoriaObservationReadOnly: true,
            },
          }
        );

        dialogRef.afterClosed().subscribe((result) => {
          this.router.navigate([
            "admission-reception-wizard",
            admissionSearchItem.MedicalAppointmentId,
          ]);
        });
      } else {
        this.router.navigate([
          "admission-reception-wizard",
          admissionSearchItem.MedicalAppointmentId,
        ]);
      }
    }
  }

  onTableResized() {}

  selectedPageIndexChange(pagedItems: any[]) {
    this.dataSource = [...pagedItems];
    this.loaded = true;
  }

  onEndSearch(response: IFilterResponse<AdmissionSearchItem>) {
    this.allItems = response.items || [];
    this.searchFilters = response.searchFilters;
    this.refreshGrid();
    this.fitVisibleColumnsToContainer();
  }
}
